import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Button3D } from '@/components/ui/button-3d';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  ArrowLeft,
  Trash2,
  AlertTriangle,
  Loader2,
  CheckCircle,
  LogIn,
  Send,
  Mail,
} from 'lucide-react';
import {
  ACCOUNT_DELETION_UNCONFIRMED_MESSAGE,
  AccountDeletionNotConfirmedError,
  requestAccountDeletion,
} from '@/lib/accountDeletion';
import { toast } from 'sonner';

export default function DeleteAccountPage() {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  
  // Logged-in user state
  const [confirmEmail, setConfirmEmail] = useState('');
  const [understood, setUnderstood] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleted, setDeleted] = useState(false);
  
  // Form state for users without access
  const [formName, setFormName] = useState('');
  const [formEmail, setFormEmail] = useState('');
  const [formReason, setFormReason] = useState('');
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);

  const handleDeleteAccount = async () => {
    if (!user) return;
    
    if (confirmEmail.toLowerCase() !== user.email?.toLowerCase()) {
      toast.warning('O e-mail digitado não corresponde ao da sua conta');
      return;
    }

    if (!understood) {
      toast.warning('Você precisa confirmar que entendeu as consequências');
      return;
    }

    setDeleting(true);

    try {
      // A EXCLUSÃO INTEIRA ACONTECE NO SERVIDOR, numa chamada só.
      //
      // Antes eram nove deletes seguidos aqui, e dois problemas de uma vez
      // (D13): `profiles` não tem política de DELETE — sob RLS o comando remove
      // ZERO linhas e responde 200 SEM ERRO —, e `auth.users` nunca era tocado,
      // então "Excluir minha conta" não excluía a conta: bastava entrar de novo
      // com o mesmo e-mail e senha.
      //
      // Apagar `auth.users` exige `service_role`, que só existe no servidor. E
      // isso resolve o problema estrutural de quebra: `profiles.id` referencia
      // `auth.users(id)` ON DELETE CASCADE e o resto pendura em `auth.users` ou
      // em `companies` (também CASCADE), então tudo cai numa TRANSAÇÃO — não há
      // meio-estado, que era o risco da sequência de deletes (origem do #109).
      //
      // A função verifica no servidor que o perfil sumiu antes de confirmar;
      // `requestAccountDeletion` lança se o corpo não confirmar, porque um
      // `invoke` que não estourou não prova exclusão nenhuma (#9).
      await requestAccountDeletion(supabase, confirmEmail);

      // Sign out
      await signOut();
      
      setDeleted(true);
      toast.success('Conta excluída com sucesso');
    } catch (error) {
      console.error('Error deleting account:', error);
      // Exclusão não confirmada pede outra mensagem: ela diz que NADA foi
      // apagado, e isso é verdade por construção — a exclusão é uma transação
      // única no servidor.
      toast.error(
        error instanceof AccountDeletionNotConfirmedError
          ? ACCOUNT_DELETION_UNCONFIRMED_MESSAGE
          : 'Erro ao excluir conta. Tente novamente.',
      );
    } finally {
      setDeleting(false);
    }
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formName.trim() || !formEmail.trim()) {
      toast.warning('Preencha todos os campos obrigatórios');
      return;
    }

    setFormSubmitting(true);

    try {
      // Create mailto link and open
      const subject = encodeURIComponent('Solicitação de exclusão de conta - Orçamigo');
      const body = encodeURIComponent(
`Solicitação de exclusão de conta

Nome completo: ${formName}
E-mail da conta: ${formEmail}
${formReason ? `Motivo: ${formReason}` : ''}

---
Solicito a exclusão permanente da minha conta e todos os dados associados no aplicativo Orçamigo.

Estou ciente de que:
- Esta ação é irreversível
- Todos os meus orçamentos serão excluídos
- Todos os meus clientes cadastrados serão excluídos
- Todo o meu catálogo será excluído
- Minhas empresas e configurações serão excluídas

Data da solicitação: ${new Date().toLocaleDateString('pt-BR')}
`
      );
      
      window.location.href = `mailto:contato@orcamigo.com.br?subject=${subject}&body=${body}`;
      
      setFormSubmitted(true);
      toast.success('E-mail aberto! Envie para completar a solicitação.');
    } catch (error) {
      console.error('Error:', error);
      toast.error('Erro ao abrir o e-mail. Tente novamente.');
    } finally {
      setFormSubmitting(false);
    }
  };

  // Success state after deletion
  if (deleted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center space-y-4">
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto" />
            <h2 className="text-xl font-semibold">Conta excluída</h2>
            <p className="text-muted-foreground">
              Sua conta e todos os dados associados foram excluídos permanentemente.
            </p>
            <p className="text-sm text-muted-foreground">
              Obrigado por ter usado o Orçamigo. Se mudar de ideia, você sempre pode criar uma nova conta.
            </p>
            <Button3D color="gray" onClick={() => navigate('/')} className="w-full mt-4">
              Voltar ao início
            </Button3D>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Form submitted state (for non-logged users)
  if (formSubmitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center space-y-4">
            <Mail className="h-16 w-16 text-primary mx-auto" />
            <h2 className="text-xl font-semibold">Solicitação enviada</h2>
            <p className="text-muted-foreground">
              Complete o envio do e-mail que foi aberto para finalizar sua solicitação.
            </p>
            <p className="text-sm text-muted-foreground">
              Nossa equipe analisará seu pedido e responderá em até 5 dias úteis.
            </p>
            <Button3D color="gray" onClick={() => navigate('/')} className="w-full mt-4">
              Voltar ao início
            </Button3D>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Not logged in state - Show form
  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-lg">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4">
              <img 
                src="https://kizuizwbhlttcmgmllst.supabase.co/storage/v1/object/public/public-app-assets/Logo/Logo%20Original%20Sem%20Fundo%20-%20Orcamigo.png" 
                alt="Orçamigo" 
                className="h-16 w-auto mx-auto"
              />
            </div>
            <CardTitle className="flex items-center justify-center gap-2 text-destructive">
              <Trash2 className="h-5 w-5" />
              Solicitar exclusão de conta
            </CardTitle>
            <CardDescription>
              Orçamigo - Formulário de solicitação de exclusão
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Instructions */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <h3 className="font-semibold text-amber-800 mb-2">📋 Como funciona:</h3>
              <ol className="text-sm text-amber-700 space-y-1 list-decimal list-inside">
                <li>Preencha o formulário abaixo com seus dados</li>
                <li>Clique em "Enviar solicitação"</li>
                <li>Um e-mail será preparado automaticamente</li>
                <li>Envie o e-mail para nossa equipe</li>
                <li>Você receberá uma confirmação em até 5 dias úteis</li>
              </ol>
            </div>

            {/* Data deletion info */}
            <div className="bg-destructive/5 border border-destructive/20 rounded-lg p-4">
              <p className="text-sm font-medium text-destructive mb-2">⚠️ Dados que serão excluídos:</p>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• Todos os seus orçamentos e histórico</li>
                <li>• Todos os clientes cadastrados</li>
                <li>• Todo o catálogo de serviços/produtos</li>
                <li>• Todas as empresas cadastradas</li>
                <li>• Todas as notificações</li>
                <li>• Suas configurações e perfil</li>
              </ul>
              <p className="text-xs text-muted-foreground mt-3">
                <strong>Período de retenção:</strong> Os dados são excluídos imediatamente após o processamento da solicitação. 
                Nenhum dado é mantido após a exclusão, exceto logs de auditoria exigidos por lei (até 6 meses).
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleFormSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Nome completo *</Label>
                <Input
                  id="name"
                  type="text"
                  placeholder="Seu nome completo"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">E-mail da conta *</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="email@exemplo.com"
                  value={formEmail}
                  onChange={(e) => setFormEmail(e.target.value)}
                  required
                />
                <p className="text-xs text-muted-foreground">
                  Use o mesmo e-mail cadastrado no app Orçamigo
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="reason">Motivo da exclusão (opcional)</Label>
                <Textarea
                  id="reason"
                  placeholder="Conte-nos por que deseja excluir sua conta..."
                  value={formReason}
                  onChange={(e) => setFormReason(e.target.value)}
                  rows={3}
                />
              </div>

              <Button3D
                type="submit"
                color="red"
                className="w-full"
                disabled={formSubmitting}
              >
                {formSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Preparando...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Enviar solicitação
                  </>
                )}
              </Button3D>
            </form>

            <div className="text-center space-y-2">
              <p className="text-sm text-muted-foreground">
                Se você ainda tem acesso à sua conta:
              </p>
              <Link to="/auth">
                <Button variant="outline" className="w-full">
                  <LogIn className="h-4 w-4 mr-2" />
                  Fazer login e excluir pelo App
                </Button>
              </Link>
            </div>

            <Button variant="ghost" className="w-full" onClick={() => navigate(-1)}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Voltar
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Logged in - Show deletion confirmation
  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4">
            <img 
              src="https://kizuizwbhlttcmgmllst.supabase.co/storage/v1/object/public/public-app-assets/Logo/Logo%20Original%20Sem%20Fundo%20-%20Orcamigo.png" 
              alt="Orçamigo" 
              className="h-12 w-auto mx-auto mb-2"
            />
          </div>
          <div className="mx-auto mb-4 p-3 rounded-full bg-destructive/10">
            <AlertTriangle className="h-8 w-8 text-destructive" />
          </div>
          <CardTitle className="text-destructive">Excluir conta permanentemente</CardTitle>
          <CardDescription>
            Esta ação é irreversível. Todos os seus dados serão apagados.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* What will be deleted */}
          <div className="bg-destructive/5 border border-destructive/20 rounded-lg p-4">
            <p className="text-sm font-medium text-destructive mb-2">Serão excluídos permanentemente:</p>
            <ul className="text-sm text-muted-foreground space-y-1">
              <li>• Todos os seus orçamentos e histórico</li>
              <li>• Todos os clientes cadastrados</li>
              <li>• Todo o catálogo de serviços/produtos</li>
              <li>• Todas as empresas cadastradas</li>
              <li>• Todas as notificações</li>
              <li>• Suas configurações e perfil</li>
            </ul>
            <p className="text-xs text-muted-foreground mt-3">
              <strong>Período de retenção:</strong> Dados excluídos imediatamente. Apenas logs de auditoria são mantidos por até 6 meses conforme exigência legal.
            </p>
          </div>

          {/* Current account info */}
          <div className="bg-muted/50 rounded-lg p-3">
            <p className="text-xs text-muted-foreground mb-1">Conta a ser excluída:</p>
            <p className="font-medium">{user.email}</p>
          </div>

          {/* Confirmation form */}
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="confirm-email">Digite seu e-mail para confirmar:</Label>
              <Input
                id="confirm-email"
                type="email"
                placeholder={user.email || 'seu@email.com'}
                value={confirmEmail}
                onChange={(e) => setConfirmEmail(e.target.value)}
              />
            </div>

            <div className="flex items-start space-x-2">
              <Checkbox
                id="understood"
                checked={understood}
                onCheckedChange={(checked) => setUnderstood(checked === true)}
              />
              <label
                htmlFor="understood"
                className="text-sm text-muted-foreground leading-tight cursor-pointer"
              >
                Entendo que esta ação é irreversível e que todos os meus dados serão permanentemente excluídos.
              </label>
            </div>
          </div>

          {/* Action buttons */}
          <div className="space-y-2">
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button3D
                  color="red"
                  className="w-full"
                  disabled={!understood || confirmEmail.toLowerCase() !== user.email?.toLowerCase()}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Excluir minha conta
                </Button3D>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Última confirmação</AlertDialogTitle>
                  <AlertDialogDescription>
                    Você está prestes a excluir permanentemente sua conta{' '}
                    <strong>{user.email}</strong>. Esta ação não pode ser desfeita.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleDeleteAccount}
                    disabled={deleting}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  >
                    {deleting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Excluindo...
                      </>
                    ) : (
                      'Sim, excluir definitivamente'
                    )}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>

            <Button3D color="gray" className="w-full" onClick={() => navigate(-1)}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Cancelar e voltar
            </Button3D>
          </div>

          {/* Note about subscriptions */}
          <p className="text-xs text-muted-foreground text-center">
            ⚠️ Se você tem uma assinatura ativa, cancele-a primeiro na Google Play Store.
            A exclusão da conta não cancela automaticamente cobranças futuras.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}